/**
 * Resolves in-browser EIP-1193 Ethereum providers.
 *
 * 1) EIP-6963 (Trust official): announceProvider + requestProvider — Trust rdns: com.trustwallet.app
 * 2) Legacy: ethereum.providers[], window.ethereum, window.trustwallet, polling
 *
 * @see https://developer.trustwallet.com/developer/develop-for-trust/browser-extension/evm
 *
 * Logging strategy: we DO NOT log on every poll attempt. We log only when:
 *  - A wallet announces (EIP-6963)
 *  - The state of `pickEip6963` / `pickLegacy` actually CHANGES (transitions)
 *  - Wait succeeds, times out, or hits one of a few coarse milestones
 */

import { uistep, uiwarn, dumpEnvironment } from '@/utils/uiLogger'

function providerFlags(p) {
  if (!p) return null
  const flags = []
  if (p.isMetaMask) flags.push('mm')
  if (p.isTrust) flags.push('trust')
  if (p.isTrustWallet) flags.push('tw')
  if (p.isCoinbaseWallet) flags.push('cb')
  if (p.isOkxWallet) flags.push('okx')
  return {
    flags: flags.length ? flags.join('+') : 'unknown',
    hasRequest: typeof p.request === 'function',
    hasOn: typeof p.on === 'function',
    chainId: p.chainId || null
  }
}

function isEip1193Provider(p) {
  return !!(p && typeof p.request === 'function')
}

/** @type {Map<string, { info: object, provider: object }>} */
const eip6963Announced = new Map()
let eip6963ListenerAttached = false
let earlyProbeReplayed = false
let primeCallCount = 0

// De-dup state: only emit a log when the pick state changes
let lastPickState = ''

function announceProviderKey(detail) {
  const u = detail && detail.info && detail.info.uuid
  if (u) return String(u)
  const rdns = (detail && detail.info && detail.info.rdns) || ''
  const name = (detail && detail.info && detail.info.name) || ''
  return rdns + '::' + name
}

/**
 * Replay any announceProvider events that the inline <head> probe captured
 * before our webpack bundle started running. Critical for SPAs where the
 * bundle takes hundreds of ms to a second to boot — Trust Wallet iOS 26.20+
 * announces at document_start, well before the bundle is alive.
 */
function replayEarlyProbe() {
  if (earlyProbeReplayed || typeof window === 'undefined') return
  earlyProbeReplayed = true
  try {
    const probe = window.__ubsEarlyWalletProbe
    if (!probe || !Array.isArray(probe.announces)) {
      uistep('EIP-6963', 'no early probe object found')
      return
    }
    let replayed = 0
    probe.announces.forEach((entry) => {
      if (!entry || !entry.detail) return
      const detail = entry.detail
      const key = announceProviderKey(detail)
      if (!key || eip6963Announced.has(key)) return
      eip6963Announced.set(key, { info: detail.info, provider: detail.provider })
      replayed++
    })
    if (replayed > 0) {
      uistep('EIP-6963', 'replayed', replayed, 'early announce(s); total=', eip6963Announced.size)
    } else {
      uistep('EIP-6963', 'early probe had 0 announces (snapshots=', probe.snapshots ? probe.snapshots.length : 0, 'requests=', probe.requestsDispatched, ')')
    }
  } catch (e) {
    uiwarn('[EIP-6963] replayEarlyProbe error:', e && e.message ? e.message : e)
  }
}

function onEip6963Announce(event) {
  try {
    const detail = event.detail
    if (!detail || !detail.info || !detail.provider) {
      uiwarn('[EIP-6963] announce IGNORED: missing detail')
      return
    }
    const info = detail.info
    const key = announceProviderKey(detail)
    if (!key || eip6963Announced.has(key)) return
    eip6963Announced.set(key, { info: detail.info, provider: detail.provider })
    uistep('EIP-6963', 'ANNOUNCE rdns=', info.rdns, 'name=', info.name, providerFlags(detail.provider), 'total=', eip6963Announced.size)
  } catch (e) {
    uiwarn('[EIP-6963] handler error:', e && e.message ? e.message : e)
  }
}

/**
 * Call early so announces are not missed. Listens on both window and document (some WebViews differ).
 * Idempotent — multiple modules call this defensively at load time, but only the first
 * call does real work (and logs). Subsequent calls just re-dispatch requestProvider silently.
 */
export function primeEip6963Discovery() {
  primeCallCount++
  if (primeCallCount === 1) {
    uistep('EIP-6963', 'prime() invoked')
    replayEarlyProbe()
    attachEip6963Listener()
    requestWalletsAnnounce(true)
  } else {
    // Subsequent calls only ask wallets to re-announce (silent).
    requestWalletsAnnounce(false)
  }
}

function attachEip6963Listener() {
  if (eip6963ListenerAttached || typeof window === 'undefined') return
  eip6963ListenerAttached = true
  window.addEventListener('eip6963:announceProvider', onEip6963Announce)
  if (typeof document !== 'undefined') {
    document.addEventListener('eip6963:announceProvider', onEip6963Announce)
  }
  uistep('EIP-6963', 'listeners attached')
}

function requestWalletsAnnounce(verbose) {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(new Event('eip6963:requestProvider'))
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new Event('eip6963:requestProvider'))
    }
    if (verbose) {
      uistep('EIP-6963', 'dispatched requestProvider')
    }
  } catch (e) {
    uiwarn('[EIP-6963] dispatch failed:', e && e.message ? e.message : e)
  }
}

/**
 * Human-readable snapshot for support. Run in console: window.__ubsDumpWalletEnv()
 */
export function dumpWalletEnvironmentForSupport() {
  if (typeof window === 'undefined') return {}
  const w = window
  const eth = w.ethereum
  const snap = {
    ua: navigator.userAgent,
    hasWindowEthereum: typeof eth !== 'undefined',
    ethereumIsTrust: !!(eth && eth.isTrust),
    ethereumIsMetaMask: !!(eth && eth.isMetaMask),
    ethereumProvidersLen: Array.isArray(eth && eth.providers) ? eth.providers.length : 0,
    hasTrustwallet: typeof w.trustwallet !== 'undefined',
    hasTrustWeb3Provider: typeof w.TrustWeb3Provider !== 'undefined',
    eip6963AnnouncedCount: eip6963Announced.size,
    eip6963RdnsList: [...eip6963Announced.values()].map((e) => e.info && e.info.rdns).filter(Boolean)
  }
  console.warn('Environment snapshot:', JSON.stringify(snap))
  return snap
}

function pickEip6963Provider(options) {
  const opts = options || {}
  const preferMetaMask = !!opts.preferMetaMask
  // We attach + replay + request silently here; only the announce HANDLER logs.
  replayEarlyProbe()
  attachEip6963Listener()
  requestWalletsAnnounce(false)

  const entries = [...eip6963Announced.values()]
  if (entries.length === 0) return null

  if (preferMetaMask) {
    const mm = entries.find((e) => e.info && e.info.rdns === 'io.metamask')
    if (mm && isEip1193Provider(mm.provider)) {
      uistep('PICK', 'MetaMask via EIP-6963')
      return mm.provider
    }
  } else {
    const tw = entries.find((e) => {
      const rdns = (e.info && e.info.rdns) || ''
      return /^com\.trustwallet/i.test(rdns)
    })
    if (tw && isEip1193Provider(tw.provider)) {
      uistep('PICK', 'Trust via EIP-6963 rdns=', tw.info && tw.info.rdns)
      return tw.provider
    }
  }

  const firstOk = entries.find((e) => isEip1193Provider(e.provider))
  if (firstOk) {
    uistep('PICK', 'first valid via EIP-6963 rdns=', firstOk.info && firstOk.info.rdns)
    return firstOk.provider
  }
  return null
}

function pickLegacyInjectedProvider(options) {
  const opts = options || {}
  const preferMetaMask = !!opts.preferMetaMask
  if (typeof window === 'undefined') return null

  const w = window
  const eth = w.ethereum

  const pickFromProviders = (providers) => {
    if (!Array.isArray(providers) || providers.length === 0) return null
    if (preferMetaMask) {
      const mm = providers.find((p) => p && p.isMetaMask === true)
      if (isEip1193Provider(mm)) return mm
    } else {
      const trust = providers.find((p) => p && (p.isTrust === true || p.isTrustWallet === true))
      if (isEip1193Provider(trust)) return trust
      const mm = providers.find((p) => p && p.isMetaMask === true)
      if (isEip1193Provider(mm)) return mm
      const cb = providers.find((p) => p && p.isCoinbaseWallet === true)
      if (isEip1193Provider(cb)) return cb
    }
    const first = providers.find((p) => isEip1193Provider(p))
    return first || null
  }

  if (isEip1193Provider(eth) && Array.isArray(eth.providers) && eth.providers.length > 0) {
    const picked = pickFromProviders(eth.providers)
    if (picked) {
      uistep('PICK', 'legacy from ethereum.providers[]', providerFlags(picked))
      return picked
    }
  }
  if (isEip1193Provider(eth)) {
    uistep('PICK', 'legacy window.ethereum', providerFlags(eth))
    return eth
  }
  if (!preferMetaMask) {
    const tw = w.trustwallet || w.trustWallet
    if (tw && typeof tw === 'object') {
      if (isEip1193Provider(tw.ethereum)) {
        uistep('PICK', 'legacy window.trustwallet.ethereum', providerFlags(tw.ethereum))
        return tw.ethereum
      }
      if (isEip1193Provider(tw)) {
        uistep('PICK', 'legacy window.trustwallet (itself)', providerFlags(tw))
        return tw
      }
    }
    if (isEip1193Provider(w.TrustWeb3Provider)) {
      uistep('PICK', 'legacy window.TrustWeb3Provider')
      return w.TrustWeb3Provider
    }
  }
  return null
}

/**
 * Build a short fingerprint of the current pick state so we only log on transitions.
 */
function pickStateFingerprint() {
  const w = typeof window !== 'undefined' ? window : null
  if (!w) return 'no-window'
  const eth = w.ethereum
  const tw = w.trustwallet || w.trustWallet
  return [
    'eth=' + (eth ? (typeof eth.request === 'function' ? '1' : '0') : '-'),
    'twNs=' + (tw && typeof tw === 'object' ? Object.keys(tw).join(',') : '-'),
    'tw3p=' + (typeof w.TrustWeb3Provider !== 'undefined' ? '1' : '0'),
    '6963=' + eip6963Announced.size
  ].join('|')
}

/**
 * @param {object} [options]
 * @param {boolean} [options.preferMetaMask]
 */
export function getBrowserEthereumProvider(options) {
  const fp = pickStateFingerprint()
  const transitioned = fp !== lastPickState
  lastPickState = fp

  const from6963 = pickEip6963Provider(options)
  if (from6963) return from6963
  const legacy = pickLegacyInjectedProvider(options)
  if (legacy) return legacy

  // Only log "no provider" when state has TRANSITIONED — avoids polling spam.
  if (transitioned) {
    uistep('PICK', 'no provider yet:', fp)
  }
  return null
}

export function hasBrowserEthereumProvider(options) {
  return getBrowserEthereumProvider(options) != null
}

/**
 * Wait for EIP-6963 announce or late legacy injection.
 */
export async function waitForBrowserEthereumProvider(options) {
  const opts = options || {}
  const timeoutMs = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : 5000
  const intervalMs = typeof opts.intervalMs === 'number' ? opts.intervalMs : 120
  const preferMetaMask = !!opts.preferMetaMask
  const start = Date.now()
  const deadline = start + timeoutMs

  let p = getBrowserEthereumProvider({ preferMetaMask })
  if (p) {
    uistep('WAIT', 'provider ready immediately', providerFlags(p))
    return p
  }
  uistep('WAIT', 'start polling timeoutMs=', timeoutMs, 'intervalMs=', intervalMs)
  let attempt = 0
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, intervalMs))
    attempt++
    p = getBrowserEthereumProvider({ preferMetaMask })
    if (p) {
      const elapsed = Date.now() - start
      uistep('WAIT', 'provider arrived after attempts=', attempt, 'elapsedMs=', elapsed, providerFlags(p))
      return p
    }
  }
  const elapsed = Date.now() - start
  uiwarn('[WAIT] TIMEOUT after', elapsed, 'ms attempts=', attempt, 'eip6963Count=', eip6963Announced.size)
  dumpEnvironment('wait:timeout', { force: true })
  return null
}
